import { ActivatedRouteSnapshot, DetachedRouteHandle, RouteReuseStrategy } from '@angular/router';
import { routeDataOf } from './guards/route-data';
import { routeRebuild } from './route-rebuild';

/**
 * Custom RouteReuseStrategy: Angular's default same-config reuse, with two named exceptions.
 *
 * The rebuild window (`routeRebuild`): when the world a page renders changed under an address the
 * reuse rule cannot tell apart, the routed subtree is RECREATED instead, whatever armed it — the
 * reasons are the one home's (`route-rebuild.ts`: Time travel's clock transition, a 2026-07-13
 * runtime finding — a mode enter/exit/instant change is a query-param-only navigation, so a reused
 * page kept rendering state loaded at the previous instant; a content-language switch in public
 * mode, or an admitted member's on a brand whose public access is ON; and either public-mode
 * transition under a public page). Recreation makes the whole world reload by construction; the
 * strategy reads the one window, never the reason.
 * In-mode browse clicks (drop + re-attach param legs) never transition the clock, so normal reuse
 * still applies to them.
 *
 * Reuse-group exception (2026-09-04): two DIFFERENT route configs that render one page — a
 * module root and an item's own address, e.g. `/insights` and `/insights/:id` — declare the same
 * `data.reuseGroup`, and a navigation between them keeps the page instance: without it the
 * first pick of a visit tore the page down and rebuilt it (the rail vanished, a skeleton replaced
 * the report, the rail re-queried), while every later pick between two addresses swapped
 * smoothly. The page's own `paramMap` subscription handles the hop. A group never spans the
 * rebuild window — Time travel still recreates.
 */
export class CustomRouteReuseStrategy implements RouteReuseStrategy {
  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    return false;
  }

  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
    // Not storing routes - we just want to prevent recreation, not cache
  }

  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    return false;
  }

  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    return null;
  }

  shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    // Inside the rebuild window the routed subtree is recreated, whatever armed it (see class doc).
    if (routeRebuild.pending) {
      return false;
    }

    // Default Angular behavior: reuse if same route config
    if (future.routeConfig === curr.routeConfig) {
      return true;
    }

    // One page, two addresses: both configs declare the same reuse group (see class doc).
    const group = routeDataOf(future.routeConfig?.data ?? {}).reuseGroup;
    return group !== undefined && group === routeDataOf(curr.routeConfig?.data ?? {}).reuseGroup;
  }
}
